import { useParams } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Group,
  Image,
  Loader,
  Center,
  Alert,
  Box,
  Stack,
  Badge,
} from "@mantine/core";
import Navigation from "@components/Navigation";
import { renderContentBlock } from "@components/blog/renderContentBlock";
import { useBlogDetail } from "@hooks/useBlogDetail";

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { blog, loading, error } = useBlogDetail(slug || "");

  if (loading) {
    return (
      <>
        <Navigation />
        <Container py="xl" mt={"72px"}>
          <Center>
            <Loader />
          </Center>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navigation />
        <Container py="xl" mt={"72px"}>
          <Alert color="red" title="Error">
            {error}
          </Alert>
        </Container>
      </>
    );
  }

  if (!blog) {
    return (
      <>
        <Navigation />
        <Container py="xl" mt={"72px"}>
          <Alert color="yellow" title="Not Found">
            Blog post not found
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <Container py="xl" mt="72px">
        <Stack gap="xs">
          <Box>
            <Title order={1} mb="md">
              {blog.title}
            </Title>

            <Group gap="lg" mb="xl">
              {blog.author?.name && (
                <Text size="sm" c="dimmed">
                  By <strong>{blog.author.name}</strong>
                </Text>
              )}
              <Text size="sm" c="dimmed">
                {new Date(blog.publishedAt).toLocaleDateString()}
              </Text>
            </Group>

            {blog.tags && blog.tags.length > 0 && (
              <Group gap="xs" mb="xl">
                {blog.tags.map((tag) => (
                  <Badge key={tag} variant="light">
                    {tag}
                  </Badge>
                ))}
              </Group>
            )}
          </Box>

          {blog.featuredImage && (
            <Box>
              <Image src={blog.featuredImage} alt={blog.title} radius="md" mb="xl" />
            </Box>
          )}

          <Box className="blog-content">
            {Array.isArray(blog.content) ? (
              <Stack gap={0}>
                {blog.content.map((block, index) => renderContentBlock(block, index))}
              </Stack>
            ) : (
              <Alert color="red" title="Content Error">
                Blog content is in an unexpected format.
              </Alert>
            )}
          </Box>
        </Stack>
      </Container>
    </>
  );
}
